import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select, Menu, MenuItem, Popover, IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import { format, subDays, startOfMonth } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import { Typography } from '@material-ui/core';
import KanbanBoard from './KanbanBoard';
import { MagnifyingGlass, Funnel, CalendarBlank } from '@phosphor-icons/react';

const useStyles = makeStyles(theme => ({

  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  button: {
    borderRadius: '10px',
  },

  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 18px 6px 18px',
    borderBottom: `1px solid ${theme.mode === "light" ? "#e5e5e5" : "#11151a"}`,
  },

  headerTitle: {
    display: 'flex',
    flexDirection: 'column',
  },

  pageTitle: {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    color: theme.mode === 'light' ? theme.palette.primary.main : '#fff',
    fontFamily: '"Poppins", sans-serif',
    letterSpacing: '0.025em',
  },
  pageSubtitle: {
    fontSize: '1.125rem',
    color: theme.mode === 'light' ? '#00000080' : '#ededed80',
    fontFamily: '"Poppins", sans-serif',
    fontWeight: 300,
    letterSpacing: '0.025em',
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  searchInput: {
    minWidth: '240px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: theme.mode === 'light' ? '#ffffff' : '#0c0c0e',
    }
  },
  filterButton: {
    borderRadius: '8px',
    backgroundColor: theme.mode === 'light' ? '#ffffff' : '#0c0c0e',
    borderColor: theme.mode === 'light' ? '#e5e5e5' : '#11151a',
    color: theme.mode === 'light' ? '#374151' : '#e5e7eb',
    textTransform: 'none',
    height: '40px',
  },
  iconButton: {
    borderRadius: '8px',
    backgroundColor: theme.mode === 'light' ? '#ffffff' : '#0c0c0e',
    border: `1px solid ${theme.mode === "light" ? "#e5e5e5" : "#11151a"}`,
    padding: '8px',
    height: '40px',
    width: '40px',
    color: theme.mode === 'light' ? '#374151' : '#e5e7eb',
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilterType, setDateFilterType] = useState("hoje");
  const [anchorElDate, setAnchorElDate] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [customFilters, setCustomFilters] = useState({
    coluna: '', valor: '', user: '', tempoEspera: ''
  });

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  // Orquestrador Inicial
  const initializeBoard = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];

      let initialLanes = [
        { id: 'lane0', title: i18n.t('tagsKanban.laneDefault'), tickets: [], color: '#757575', pageNumber: 1, hasMore: true, isLoading: false },
        ...fetchedTags.map(tag => ({
          id: tag.id.toString(),
          title: tag.name,
          tickets: [],
          color: tag.color || '#757575',
          pageNumber: 1,
          hasMore: true,
          isLoading: false
        })),
        { id: 'closed', title: 'Finalizados', tickets: [], color: '#10B981', pageNumber: 1, hasMore: true, isLoading: false }
      ];

      if (laneOrder && laneOrder.length > 0) {
        const orderedLanes = [];
        const laneMap = new Map(initialLanes.map(lane => [lane.id, lane]));
        laneOrder.forEach(laneId => {
          if (laneMap.has(laneId)) {
            orderedLanes.push(laneMap.get(laneId));
            laneMap.delete(laneId);
          }
        });
        laneMap.forEach(lane => orderedLanes.push(lane));
        initialLanes = orderedLanes;
      }

      setLanes(initialLanes);
      initialLanes.forEach(lane => fetchTicketsForLane(lane.id, 1, true));
    } catch (error) {
      console.log(error);
    }
  };

  // Busca Inteligente por Coluna
  const fetchTicketsForLane = async (laneId, page = 1, replace = false) => {
    setLanes(prev => prev.map(l => l.id === laneId ? { ...l, isLoading: true } : l));
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: { 
          queueIds: JSON.stringify(queueIds), 
          startDate, 
          endDate, 
          laneId, 
          pageNumber: page, 
          limit: 20,
          searchParam: searchQuery,
          filterColumn: customFilters.coluna,
          filterValue: customFilters.valor,
          filterUser: customFilters.user,
          filterWaitTime: customFilters.tempoEspera
        },
      });
      
      setLanes(prev => prev.map(l => {
        if (l.id === laneId) {
          const combined = replace ? data.tickets : [...l.tickets, ...data.tickets];
          const uniqueTickets = Array.from(new Map(combined.map(t => [t.id, t])).values());
          return {
            ...l,
            tickets: sortTickets(uniqueTickets),
            pageNumber: page,
            hasMore: data.hasMore,
            isLoading: false
          };
        }
        return l;
      }));
    } catch (err) {
      setLanes(prev => prev.map(l => l.id === laneId ? { ...l, isLoading: false } : l));
    }
  };

  const handleLoadMore = (laneId, nextPage) => {
    fetchTicketsForLane(laneId, nextPage, false);
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      const ticket = data.ticket || data.message?.ticket;
      const ticketId = data.ticketId || data.message?.ticketId || ticket?.id;

      if (['create', 'update'].includes(data.action)) {
        if (!ticket) return;

        setLanes(prevLanes => {
          // Descobre qual é a lane (coluna) correta para este ticket
          let targetLaneId = 'lane0'; // Cai em "Sem tag" por padrão
          
          if (ticket.status === 'closed') {
            targetLaneId = 'closed';
          } else if (ticket.tags && ticket.tags.length > 0) {
            // Procura se alguma tag do ticket corresponde a uma coluna existente do Board
            const kanbanTag = ticket.tags.find(tag => prevLanes.some(l => l.id === tag.id.toString() && l.id !== 'closed'));
            if (kanbanTag) targetLaneId = kanbanTag.id.toString();
          }

          return prevLanes.map(lane => {
            // 1. Apaga esse ticket de TODAS as colunas para evitar duplicação ou versão fantasma
            const filteredTickets = lane.tickets.filter(t => t.id !== ticket.id);
            
            // 2. Se esta for a coluna correta, adiciona o ticket e reordena!
            if (lane.id === targetLaneId) return { ...lane, tickets: sortTickets([...filteredTickets, ticket]) };
            
            return { ...lane, tickets: filteredTickets };
          });
        });
      }
      if (data.action === 'delete') {
        if (!ticketId) return;
        setLanes(prevLanes => prevLanes.map(lane => ({
          ...lane,
          tickets: lane.tickets.filter(t => t.id !== ticketId)
        })));
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    lanes.forEach(lane => fetchTicketsForLane(lane.id, 1, true));
  };

  const applyDateFilter = (type) => {
    setDateFilterType(type);
    setAnchorElDate(null);
    const today = new Date();
    
    if (type === 'hoje') {
      setStartDate(format(today, 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    } else if (type === '7days') {
      setStartDate(format(subDays(today, 7), 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    } else if (type === 'month') {
      setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
      setEndDate(format(today, 'yyyy-MM-dd'));
    }
    // Se for 'custom', não fazemos nada automático, o usuário escolhe nos DatePickers que aparecerão
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setLanes(prevLanes => prevLanes.map(lane => ({
      ...lane,
      tickets: lane.tickets.map(t => t.id === updatedTicket.id ? updatedTicket : t)
    })));
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const sortTickets = (ticketsArray) => {
    const sorted = [...ticketsArray];

    /* LEAD SCORING: Ordenação por Score
    if (sortOrder === 'scoreDesc') {
      sorted.sort((a, b) => {
        const scoreA = a.scoringInfo?.leadScore || 0;
        const scoreB = b.scoringInfo?.leadScore || 0;
        return scoreB - scoreA;
      });
    } else */ if (sortOrder === 'ticketNumber') {
      sorted.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sorted.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else if (sortOrder === 'valorDesc') {
      sorted.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }
    return sorted;
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      initializeBoard();
    }
  }, [sortOrder, laneOrder, loadingLaneOrder]);

  const handleCardMove = async (ticketId, targetLaneId, sourceLaneId, sourceIndex, destinationIndex) => {
    ticketId = parseInt(ticketId, 10);
    
    // Atualização Visual Imediata (Optimistic UI) para não piscar a tela
    setLanes(prevLanes => {
      let movedTicket = null;
      
      const tempLanes = prevLanes.map(lane => {
        if (lane.id === sourceLaneId) {
          const newTickets = Array.from(lane.tickets);
          const tIndex = newTickets.findIndex(t => t.id === ticketId);
          if (tIndex !== -1) {
            movedTicket = newTickets.splice(tIndex, 1)[0];
          }
          return { ...lane, tickets: newTickets };
        }
        return lane;
      });

      if (!movedTicket) return prevLanes;

      return tempLanes.map(lane => {
        if (lane.id === targetLaneId) {
          const newTickets = Array.from(lane.tickets);
          // Insere exatamente onde o usuário soltou para o react-beautiful-dnd não quebrar
          newTickets.splice(destinationIndex, 0, movedTicket);
          return { ...lane, tickets: newTickets };
        }
        return lane;
      });
    });

    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        // toast.success('Ticket Tag atualizado com sucesso!');
      } else {
        // toast.success('Ticket Tag removido!');
      }
    } catch (err) {
      toast.error('Erro ao mover ticket');
      fetchTicketsForLane(sourceLaneId, 1, true); // Retorno seguro em caso de falha de internet
      fetchTicketsForLane(targetLaneId, 1, true);
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  /* LEAD SCORING: Navegação para configuração de temperatura
  const handleConfigTemperaturaClick = () => {
    history.push('/lead-scoring');
  };
  */

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      // toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  return (
    <MainContainer >
      <div className={classes.headerContainer}>
        <div className={classes.headerTitle}>
          <Typography className={classes.pageTitle}>{i18n.t('Kanban')}</Typography>
          <Typography className={classes.pageSubtitle}>Gerencie suas vendas e clientes</Typography>
        </div>
        <div className={classes.headerButtons}>
          
          <TextField
            placeholder="Pesquisar (User, Cliente, ID, Ticket)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchClick()}
            size="small"
            variant="outlined"
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlass size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            className={classes.filterButton}
            onClick={(e) => setAnchorElDate(e.currentTarget)}
            startIcon={<CalendarBlank size={18} />}
          >
            {dateFilterType === 'hoje' ? 'Hoje' : dateFilterType === '7days' ? 'Últimos 7 dias' : dateFilterType === 'month' ? 'Este Mês' : 'Personalizado'}
          </Button>

          <Menu
            anchorEl={anchorElDate}
            keepMounted
            open={Boolean(anchorElDate)}
            onClose={() => setAnchorElDate(null)}
          >
            <MenuItem onClick={() => applyDateFilter('hoje')}>Hoje</MenuItem>
            <MenuItem onClick={() => applyDateFilter('7days')}>Últimos 7 dias</MenuItem>
            <MenuItem onClick={() => applyDateFilter('month')}>Este Mês</MenuItem>
            <MenuItem onClick={() => applyDateFilter('custom')}>Personalizado</MenuItem>
          </Menu>

          {dateFilterType === 'custom' && (
            <>
              <TextField label="Início" type="date" value={startDate} onChange={handleStartDateChange} InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
              <TextField label="Fim" type="date" value={endDate} onChange={handleEndDateChange} InputLabelProps={{ shrink: true }} variant="outlined" size="small" />
            </>
          )}

          <Tooltip title="Filtros Avançados">
            <IconButton onClick={(e) => setAnchorElFilter(e.currentTarget)} className={classes.iconButton}>
              <Funnel size={20} />
            </IconButton>
          </Tooltip>

          <Popover
            open={Boolean(anchorElFilter)}
            anchorEl={anchorElFilter}
            onClose={() => setAnchorElFilter(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 280 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Filtros & Ordenação</Typography>

              <FormControl variant="outlined" size="small">
                <InputLabel>Ordenar por</InputLabel>
                <Select native value={sortOrder} onChange={handleSortOrderChange} label="Ordenar por">
                  <option value="ticketNumber">Número do Ticket</option>
                  <option value="lastMessageTime">Última Mensagem</option>
                  <option value="valorDesc">Valor (maior para menor)</option>
                </Select>
              </FormControl>

              <TextField label="Filtrar Coluna" size="small" variant="outlined" value={customFilters.coluna} onChange={e => setCustomFilters({...customFilters, coluna: e.target.value})} />
              <TextField label="Valor a partir de (R$)" type="number" size="small" variant="outlined" value={customFilters.valor} onChange={e => setCustomFilters({...customFilters, valor: e.target.value})} />
              <TextField label="Usuário Responsável" size="small" variant="outlined" value={customFilters.user} onChange={e => setCustomFilters({...customFilters, user: e.target.value})} />
              <TextField label="Tempo de Espera (min)" type="number" size="small" variant="outlined" value={customFilters.tempoEspera} onChange={e => setCustomFilters({...customFilters, tempoEspera: e.target.value})} />

              <Button variant="contained" color="primary" onClick={() => { setAnchorElFilter(null); handleSearchClick(); }} style={{ borderRadius: 8, marginTop: 8 }}>
                Aplicar Filtros
              </Button>
            </div>
          </Popover>

          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <>
                {/* LEAD SCORING: Botão de configuração */}
                {/* <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfigTemperaturaClick}
                  className={classes.button}
                  style={{ marginRight: '8px' }}
                >
                  Config. Temperatura
                </Button> */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddColumnClick}
                  className={classes.button}
                >
                  + Novas colunas
                </Button>
              </>
            )}
          />
        </div>
      </div>
      <div className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          onLoadMore={handleLoadMore}
          isAdmin={user.profile === 'admin'}
        />
      </div>
    </MainContainer>
  );
};

export default Kanban;
